import React, { useEffect, useState } from 'react';
import { Typography, Breadcrumb, Card, List, Button, Space, Tag, message, Popconfirm, Modal, Form, Input, Select } from 'antd';
import { useAuth } from '../../../contexts/AuthContext';

const { Title, Paragraph } = Typography;

const TemplatesPresets = () => {
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState([]);
  const [category, setCategory] = useState('email');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState('create'); // 'create' | 'edit'
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [templateForm] = Form.useForm();
  const [htmlImporting, setHtmlImporting] = useState(false);
  const [previewVisible, setPreviewVisible] = useState(false);
  const { token } = useAuth();

  useEffect(() => {
    const load = async () => {
      try {
        if (!token) return;
        setLoading(true);
        const params = new URLSearchParams();
        if (category) params.set('category', category);
        const res = await fetch(`/api/templates?${params.toString()}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) throw new Error('Failed to load templates');
        const data = await res.json();
        setItems(data || []);
      } catch (e) {
        message.error(e?.message || 'Failed to load templates');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [token, category]);

  const openCreate = () => {
    const activeCat = 'email';
    setCategory('email');
    templateForm.resetFields();
    // sensible defaults per category
    if (activeCat === 'email') {
      templateForm.setFieldsValue({ name: 'New Email', type: 'email', size: '600x', emailPreset: 'newsletter', subject: 'Our latest news', preheader: 'Catch up with updates', content: '' });
    } else {
      templateForm.setFieldsValue({ name: 'New Campaign', type: 'image', size: '1080x1080', aspectRatio: '1:1', platform: 'facebook' });
    }
    setModalMode('create');
    setEditingTemplate(null);
    setIsModalOpen(true);
  };

  const openEdit = (template) => {
    setCategory(template.category || 'email');
    setModalMode('edit');
    setEditingTemplate(template);
    const baseValues = {
      name: template.name,
      type: template.type || 'email',
      size: template.size || '600x',
      emailPreset: template.metadata?.preset || null,
      subject: template.metadata?.subject || '',
      preheader: template.metadata?.preheader || '',
      aspectRatio: template.metadata?.aspectRatio || '',
      platform: template.metadata?.platform || '',
      content: template.content || '',
    };
    templateForm.setFieldsValue(baseValues);
    setIsModalOpen(true);
    const effectiveCategory = template.category || 'email';
    if (effectiveCategory === 'email' && (template.content && template.content.trim().length > 0)) {
      setPreviewVisible(true);
    }
  };

  const buildPayloadFromValues = (values) => {
    const metadata = category === 'email'
      ? { preset: values.emailPreset || null, subject: values.subject || '', preheader: values.preheader || '' }
      : { aspectRatio: values.aspectRatio || '', platform: values.platform || '' };
    return {
      name: values.name,
      type: values.type,
      size: values.size,
      category,
      metadata,
      content: category === 'email' ? (values.content || '') : null,
    };
  };

  const submitModal = async () => {
    try {
      const values = await templateForm.validateFields();
      if (!token) throw new Error('Not authenticated');
      const payload = buildPayloadFromValues(values);

      if (modalMode === 'create') {
        const res = await fetch('/api/templates', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(payload),
        });
        if (!res.ok) throw new Error('Failed to create template');
        const created = await res.json();
        setItems(prev => [created, ...prev]);
        message.success('Template created');
      } else if (editingTemplate) {
        const res = await fetch(`/api/templates/${editingTemplate.id}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(payload),
        });
        if (!res.ok) throw new Error('Failed to update template');
        const updated = await res.json();
        setItems(prev => prev.map(it => (String(it.id) === String(updated.id) ? updated : it)));
        message.success('Template updated');
      }
      setIsModalOpen(false);
      setEditingTemplate(null);
    } catch (e) {
      if (e?.errorFields) return; // form errors already shown
      message.error(e?.message || 'Create failed');
    }
  };

  const onDuplicate = async (id) => {
    try {
      if (!token) throw new Error('Not authenticated');
      const res = await fetch(`/api/templates/${id}/duplicate`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Failed to duplicate template');
      const dup = await res.json();
      setItems(prev => [dup, ...prev]);
      message.success('Template duplicated');
    } catch (e) {
      message.error(e?.message || 'Duplicate failed');
    }
  };

  const onDelete = async (id) => {
    try {
      if (!token) throw new Error('Not authenticated');
      const res = await fetch(`/api/templates/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Failed to delete template');
      setItems(prev => prev.filter(it => String(it.id) !== String(id)));
      message.success('Template deleted');
    } catch (e) {
      message.error(e?.message || 'Delete failed');
    }
  };

  return (
    <div style={{ padding: 24 }}>
      <Breadcrumb items={[{ title: 'Client' }, { title: 'Creatives' }, { title: 'Templates & Presets' }]} />

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 12, marginBottom: 16 }}>
        <Title level={3} style={{ margin: 0 }}>Templates & Presets</Title>
        <Space>
          <Button type="primary" onClick={openCreate}>Create Email Template</Button>
        </Space>
      </div>

      <Card>
        <Paragraph type="secondary" style={{ marginBottom: 12 }}>Start from ready-made templates and presets.</Paragraph>
        <List
          loading={loading}
          itemLayout="horizontal"
          dataSource={items}
          renderItem={(item) => (
            <List.Item actions={[
              <Button size="small" key="edit" onClick={() => openEdit(item)}>Edit</Button>,
              <Button size="small" key="duplicate" onClick={() => onDuplicate(item.id)}>Duplicate</Button>,
              <Popconfirm key="delete" title="Delete this template?" onConfirm={() => onDelete(item.id)} okText="Delete" okButtonProps={{ danger: true }}>
                <Button size="small" danger>Delete</Button>
              </Popconfirm>
            ]}>
              <List.Item.Meta
                title={item.name}
                description={
                  <span>
                    <Tag>{item.type}</Tag> <Tag>{item.size}</Tag> {item.category ? <Tag color="blue">{item.category}</Tag> : null}
                    {item.category === 'email' && item.metadata ? (
                      <>
                        {item.metadata.preset ? <Tag color="green">{item.metadata.preset}</Tag> : null}
                        {item.metadata.subject ? <Tag color="purple">{item.metadata.subject}</Tag> : null}
                      </>
                    ) : null}
                  </span>
                }
              />
            </List.Item>
          )}
        />
      </Card>

      <Modal
        title={modalMode === 'create' ? 'Create Template' : 'Edit Template'}
        open={isModalOpen}
        onOk={submitModal}
        onCancel={() => {
          setIsModalOpen(false);
          setEditingTemplate(null);
          setPreviewVisible(false);
        }}
        destroyOnClose
        width={1000}
      >
        {category === 'email' ? (
          <div style={{ display: 'flex', gap: 24 }}>
            <div style={{ flex: 1 }}>
              <Form form={templateForm} layout="vertical">
          <Form.Item
            label="Name"
            name="name"
            rules={[{ required: true, message: 'Please enter a name' }]}
          >
            <Input placeholder="Template name" />
          </Form.Item>
          <Form.Item
            label="Type"
            name="type"
            rules={[{ required: true, message: 'Please select a type' }]}
          >
            <Select options={[{ value: 'email', label: 'Email' }, { value: 'image', label: 'Image' }, { value: 'video', label: 'Video' }]} />
          </Form.Item>
          <Form.Item
            label="Size"
            name="size"
            tooltip={
              category === 'email'
                ? <span style={{ color: '#000000' }}>Typical email width is ~600px</span>
                : 'e.g., 1080x1080, 1200x628'
            }
          >
            <Input placeholder={category === 'email' ? 'e.g., 600x' : 'e.g., 1080x1080'} />
          </Form.Item>
          {category === 'email' ? (
            <>
              <Form.Item label="Preset" name="emailPreset">
                <Select
                  options={[
                    { value: 'newsletter', label: 'Newsletter' },
                    { value: 'promotion', label: 'Promotion' },
                    { value: 'transactional', label: 'Transactional' },
                  ]}
                  onChange={(val) => {
                    if (val === 'newsletter') {
                      templateForm.setFieldsValue({ subject: 'Our latest news', preheader: 'Catch up with updates' });
                    } else if (val === 'promotion') {
                      templateForm.setFieldsValue({ subject: 'Limited-time offer inside', preheader: 'Save big before it ends' });
                    } else if (val === 'transactional') {
                      templateForm.setFieldsValue({ subject: 'Your receipt', preheader: 'Thanks for your purchase' });
                    }
                  }}
                />
              </Form.Item>
              <Form.Item label="Subject" name="subject">
                <Input placeholder="Email subject" />
              </Form.Item>
              <Form.Item label="Preheader" name="preheader">
                <Input placeholder="Email preheader" />
              </Form.Item>
              <Form.Item label="Email HTML" name="content">
                <Input.TextArea rows={8} placeholder="Paste HTML or use Upload" />
              </Form.Item>
              <Form.Item>
                <Button
                  type="default"
                  onClick={() => {
                    const html = templateForm.getFieldValue('content');
                    if (!html) {
                      message.warning('No HTML content to preview');
                      return;
                    }
                    setPreviewVisible(true);
                  }}
                >
                  Preview Email
                </Button>
              </Form.Item>
              <Form.Item label="Upload HTML">
                <input
                  type="file"
                  accept=".html,.htm,.txt"
                  onChange={async (e) => {
                    const file = e.target.files?.[0];
                    if (!file) return;
                    setHtmlImporting(true);
                    try {
                      const text = await file.text();
                      templateForm.setFieldsValue({ content: text });
                      message.success('HTML imported');
                    } catch {
                      message.error('Failed to read file');
                    } finally {
                      setHtmlImporting(false);
                      e.target.value = '';
                    }
                  }}
                  disabled={htmlImporting}
                />
              </Form.Item>
            </>
          ) : null}
          </Form>
            </div>
            {previewVisible && (
              <div style={{ flex: 1 }}>
                <Title level={5} style={{ marginBottom: 8 }}>Preview</Title>
                <div
                  style={{
                    border: '1px solid #e5e7eb',
                    borderRadius: 4,
                    padding: 16,
                    maxHeight: 500,
                    overflow: 'auto',
                    background: '#ffffff',
                    color: '#000000',
                  }}
                  dangerouslySetInnerHTML={{ __html: templateForm.getFieldValue('content') || '' }}
                />
              </div>
            )}
          </div>
        ) : (
          <Form form={templateForm} layout="vertical">
            <Form.Item
              label="Name"
              name="name"
              rules={[{ required: true, message: 'Please enter a name' }]}
            >
              <Input placeholder="Template name" />
            </Form.Item>
            <Form.Item
              label="Type"
              name="type"
              rules={[{ required: true, message: 'Please select a type' }]}
            >
              <Select options={[{ value: 'image', label: 'Image' }, { value: 'video', label: 'Video' }]} />
            </Form.Item>
            <Form.Item
              label="Size"
              name="size"
              tooltip="e.g., 1080x1080, 1200x628"
            >
              <Input placeholder="e.g., 1080x1080" />
            </Form.Item>
            <Form.Item label="Aspect Ratio" name="aspectRatio">
              <Select
                options={[
                  { value: '1:1', label: '1:1 (Square)' },
                  { value: '4:5', label: '4:5 (Portrait)' },
                  { value: '16:9', label: '16:9 (Landscape)' },
                ]}
              />
            </Form.Item>
            <Form.Item label="Platform" name="platform">
              <Select
                options={[
                  { value: 'facebook', label: 'Facebook' },
                  { value: 'instagram', label: 'Instagram' },
                  { value: 'google', label: 'Google' },
                ]}
              />
            </Form.Item>
          </Form>
        )}
      </Modal>
    </div>
  );
};

export default TemplatesPresets;
